// The narrowing lives in the URL hash beside the tab (kanban 778cc5a0, the
// Alpha Team's first official use): a standing meeting opens on the List
// filtered to its team and grouped by mandate, so
// "#initiatives?team=Alpha+team&group=mandate" is a link the meeting invite
// carries, not four clicks at the start of every meeting. Only what a link is
// for travels: the team(s), the person, the status and the grouping. The Find
// text, the health tile and Mine stay on the page (typing would rewrite the
// URL on every key; Mine is who is reading, not what is shown). The Meeting
// view's pick rides along as `series=` (kanban 4d581b21): a team's meeting
// is already said by `team=`, but the whole-roadmap meeting has no team to
// say it with, and a pick must survive the Team filter being cleared.

import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_FILTERS, STATUS_LABELS, type RoadmapFilters, type ViewBy } from './roadmapViewUtils';

const VIEW_BY: readonly ViewBy[] = ['list', 'team', 'mandate', 'person', 'actions'];

export interface HashParts {
  /** The tab id as written ("initiatives"), '' when the hash is empty. */
  tab: string;
  /** '' or the "?team=…" part, kept verbatim. */
  query: string;
}

/** "#initiatives?team=Alpha+team" → { tab: 'initiatives', query: '?team=Alpha+team' }. */
export function splitHash(hash: string): HashParts {
  const h = hash.trim().replace(/^#/, '');
  const at = h.indexOf('?');
  return at < 0 ? { tab: h.trim(), query: '' } : { tab: h.slice(0, at).trim(), query: h.slice(at) };
}

export interface HashView {
  /** Only the keys the hash names; the page folds them over the defaults. */
  filters: Partial<Pick<RoadmapFilters, 'teams' | 'person' | 'status'>>;
  /** null = the hash says nothing about grouping. */
  viewBy: ViewBy | null;
  /** The meeting series picked ('alpha', 'mgmt' …), as written; null = the hash says nothing.
   * The page checks it against the series the reader may open. */
  series: string | null;
}

/** What the hash asks for. Unknown values are ignored, never applied. */
export function viewFromHash(hash: string): HashView {
  const params = new URLSearchParams(splitHash(hash).query);
  const filters: HashView['filters'] = {};
  const teams = params.getAll('team').map((t) => t.trim()).filter(Boolean);
  if (teams.length > 0) filters.teams = [...new Set(teams)];
  const person = (params.get('person') ?? '').trim();
  if (person) filters.person = person;
  const status = (params.get('status') ?? '').trim();
  if (status in STATUS_LABELS) filters.status = status as RoadmapFilters['status'];
  const group = (params.get('group') ?? '').trim();
  const viewBy = (VIEW_BY as readonly string[]).includes(group) ? (group as ViewBy) : null;
  const series = (params.get('series') ?? '').trim() || null;
  return { filters, viewBy, series };
}

/** The "?team=…&group=…&series=…" for a narrowing; '' when nothing is narrowed, the list is ungrouped and no meeting is picked. */
export function queryFor(filters: RoadmapFilters, viewBy: ViewBy, series: string | null = null): string {
  const params = new URLSearchParams();
  for (const t of filters.teams) params.append('team', t);
  if (filters.person) params.set('person', filters.person);
  if (filters.status !== 'all') params.set('status', filters.status);
  if (viewBy !== 'list') params.set('group', viewBy);
  if (series) params.set('series', series);
  const q = params.toString();
  return q ? `?${q}` : '';
}

/** The hash the page writes for a tab with the current narrowing. */
export function hashFor(tab: string, filters: RoadmapFilters, viewBy: ViewBy, series: string | null = null): string {
  return `#${tab}${queryFor(filters, viewBy, series)}`;
}

function currentHash(): string {
  return typeof window === 'undefined' ? '' : window.location.hash;
}

function fold(view: HashView): { filters: RoadmapFilters; viewBy: ViewBy; series: string | null } {
  return { filters: { ...DEFAULT_FILTERS, ...view.filters }, viewBy: view.viewBy ?? 'list', series: view.series };
}

export type HashFilters = [
  RoadmapFilters, (f: RoadmapFilters) => void,
  ViewBy, (v: ViewBy) => void,
  /** The picked meeting series as the hash spells it (null = none picked), and its setter. */
  string | null, (s: string | null) => void,
];

/**
 * The filters and grouping, read from the hash on load and written back as
 * they change (replaceState, so Back walks the tabs and not every chip
 * click). Back / forward and a hand-edited hash re-apply what the URL says.
 * `tab` is the active tab, kept in the hash by useHashTab; a hash with no
 * tab part is written with it so the link stays a whole one.
 */
export function useHashFilters(tab: string): HashFilters {
  const [state, setState] = useState(() => fold(viewFromHash(currentHash())));
  const setFilters = useCallback((filters: RoadmapFilters) => setState((s) => ({ ...s, filters })), []);
  const setViewBy = useCallback((viewBy: ViewBy) => setState((s) => ({ ...s, viewBy })), []);
  const setSeries = useCallback((series: string | null) => setState((s) => ({ ...s, series })), []);

  useEffect(() => {
    const { tab: inHash, query } = splitHash(window.location.hash);
    const next = queryFor(state.filters, state.viewBy, state.series);
    if (next === query) return;
    // The pop-up's {roadmapPopup} entry stays what it was: only the hash text changes.
    window.history.replaceState(window.history.state, '', `#${inHash || tab}${next}`);
  }, [state.filters, state.viewBy, state.series, tab]);

  useEffect(() => {
    const onHash = () => {
      const view = viewFromHash(window.location.hash);
      setState((s) => ({
        filters: { ...s.filters, teams: view.filters.teams ?? [], person: view.filters.person ?? null, status: view.filters.status ?? 'all' },
        viewBy: view.viewBy ?? 'list',
        series: view.series,
      }));
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  return [state.filters, setFilters, state.viewBy, setViewBy, state.series, setSeries];
}
